import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Chip, IconButton, TextField, Alert,
  Skeleton, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Button,
} from '@mui/material';
import {
  SearchOutlined, PeopleOutlined, CheckCircleOutlined,
  RadioButtonUncheckedOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../hooks/useApi';
import { fmtDate } from '../components/DashboardShared';

const PRIMARY = '#1075BA';

function InitialsAvatar({ firstName, lastName }) {
  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  const hue = ((firstName?.charCodeAt(0) || 65) * 13) % 360;
  return (
    <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700, bgcolor: `hsl(${hue},50%,88%)`, color: `hsl(${hue},50%,35%)` }}>
      {initials || '?'}
    </Avatar>
  );
}

export default function ParticipantsPage() {
  const [participants, setParticipants] = useState([]);
  const [events, setEvents]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [eventFilter, setEventFilter]   = useState('');
  const [error, setError]               = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      // Load events for the filter dropdown
      const eRes = await api.get('/events');
      setEvents(eRes.data.data || []);

      // Participants endpoint — may 404 if not yet implemented, handle gracefully
      try {
        const pRes = await api.get('/participants');
        setParticipants(pRes.data.data || []);
      } catch (pe) {
        if (pe?.response?.status !== 404) throw pe;
        // 404 = endpoint not yet added — set empty, show info banner
        setParticipants([]);
        setError('ENDPOINT_MISSING');
      }
    } catch (e) {
      if (e?.response?.status !== 404) setError(e?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = participants.filter(p => {
    const q = search.toLowerCase();
    const ms = !search || `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q) || p.employeeNumber?.toLowerCase().includes(q);
    // If the participant has event sessions, filter by event
    const me = !eventFilter; // TODO: filter by event once event association is exposed
    return ms && me;
  });

  const isMissing = error === 'ENDPOINT_MISSING';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800 }}>Participants</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${participants.length} participant${participants.length !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search by name, email or ID…" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by event</InputLabel>
            <Select value={eventFilter} label="Filter by event" onChange={e => setEventFilter(e.target.value)}>
              <MenuItem value="">All events</MenuItem>
              {events.map(ev => <MenuItem key={ev._id} value={ev._id}>{ev.name}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton onClick={load} size="small" sx={{ border: `1px solid ${alpha(PRIMARY, 0.2)}`, borderRadius: 2, width: 36, height: 36 }}>
            <RefreshOutlined sx={{ fontSize: 18, color: PRIMARY }} />
          </IconButton>
        </Box>
      </Box>

      {isMissing && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <strong>Backend endpoint missing.</strong> Add <code>GET /api/participants</code> (protected with <code>authAdmin</code> + <code>requirePermission('participants:view')</code>) to your backend. Scoped to <code>req.organization._id</code>.
        </Alert>
      )}
      {error && !isMissing && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 2.5 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                <Skeleton variant="circular" width={34} height={34} />
                <Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="28%" height={14} /></Box>
                <Skeleton width={80} /><Skeleton width={60} height={20} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PeopleOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.22), mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              {isMissing ? 'Endpoint not yet implemented — see banner above.' : search ? 'No participants match.' : 'No participants yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%' }}>Participant</TableCell>
                  <TableCell sx={{ width: '22%' }}>Email</TableCell>
                  <TableCell sx={{ width: '12%' }}>Employee #</TableCell>
                  <TableCell sx={{ width: '10%' }}>Account</TableCell>
                  <TableCell sx={{ width: '10%' }}>Verified</TableCell>
                  <TableCell sx={{ width: '10%' }}>Active</TableCell>
                  <TableCell sx={{ width: '14%' }}>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p._id} hover sx={{ '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InitialsAvatar firstName={p.firstName} lastName={p.lastName} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {p.firstName} {p.lastName}
                          </Typography>
                          {p.tags?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                              {p.tags.slice(0, 2).map(tag => (
                                <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.62rem', height: 16, background: alpha('#00a882', 0.08), color: '#007a60', '& .MuiChip-label': { px: 0.75 } }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{p.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{p.employeeNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.hasAccount ? 'Account' : 'Guest'}
                        size="small"
                        sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600,
                          background: p.hasAccount ? alpha(PRIMARY, 0.07) : alpha('#888', 0.07),
                          color: p.hasAccount ? PRIMARY : '#888',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {p.isVerified
                        ? <CheckCircleOutlined sx={{ fontSize: 18, color: '#00a882' }} />
                        : <RadioButtonUncheckedOutlined sx={{ fontSize: 18, color: alpha('#000', 0.2) }} />
                      }
                    </TableCell>
                    <TableCell>
                      {p.isActive
                        ? <CheckCircleOutlined sx={{ fontSize: 18, color: '#00a882' }} />
                        : <RadioButtonUncheckedOutlined sx={{ fontSize: 18, color: alpha('#000', 0.2) }} />
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{fmtDate(p.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}